/**
 * MentionAutocomplete — dropdown for @mentions in TERRA chat input.
 *
 * Triggered by typing '@' in the textarea. Shows sites, wells, and analytes
 * with keyboard navigation (arrow keys, Enter, Escape).
 */
import { useState, useEffect, useCallback, useRef } from 'react';

interface MentionItem {
  id?: string;
  name: string;
  type: 'site' | 'well' | 'analyte';
}

const TYPE_ICONS: Record<string, string> = {
  site: '\u{1F4CD}',    // 📍
  well: '\u{1F535}',    // 🔵
  analyte: '\u{1F9EA}', // 🧪
};

const TYPE_LABELS: Record<string, string> = {
  site: 'Site',
  well: 'Well',
  analyte: 'Analyte',
};

interface Props {
  query: string;           // Text after the '@'
  onSelect: (item: MentionItem) => void;
  onClose: () => void;
  visible: boolean;
}

export function MentionAutocomplete({ query, onSelect, onClose, visible }: Props) {
  const [items, setItems] = useState<MentionItem[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Fetch autocomplete results
  useEffect(() => {
    if (!visible || !query) {
      setItems([]);
      return;
    }

    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const { apiClient } = await import('@/api/client');
        // Search all three types in parallel
        const [sites, wells, analytes] = await Promise.all([
          apiClient.get('/terra/autocomplete', { params: { q: query, type: 'site', limit: 4 } }).then(r => r.data.items).catch(() => []),
          apiClient.get('/terra/autocomplete', { params: { q: query, type: 'well', limit: 3 } }).then(r => r.data.items).catch(() => []),
          apiClient.get('/terra/autocomplete', { params: { q: query, type: 'analyte', limit: 3 } }).then(r => r.data.items).catch(() => []),
        ]);
        setItems([...sites, ...wells, ...analytes]);
        setSelectedIndex(0);
      } catch {
        setItems([]);
      } finally {
        setLoading(false);
      }
    }, 200); // debounce

    return () => clearTimeout(timer);
  }, [query, visible]);

  // Keyboard handler (called from parent textarea)
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!visible || items.length === 0) return false;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(i => (i + 1) % items.length);
      return true;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(i => (i - 1 + items.length) % items.length);
      return true;
    }
    if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      onSelect(items[selectedIndex]);
      return true;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
      return true;
    }
    return false;
  }, [visible, items, selectedIndex, onSelect, onClose]);

  if (!visible || (!loading && items.length === 0 && query.length > 0)) return null;
  if (!visible) return null;

  return (
    <div
      ref={containerRef}
      className="absolute bottom-full left-0 right-0 mb-1 bg-mono-800 border border-mono-700/50 rounded-lg shadow-xl overflow-hidden z-50"
    >
      {loading && items.length === 0 && (
        <div className="px-3 py-2 text-xs text-mono-500">Searching...</div>
      )}
      {items.map((item, i) => (
        <button
          key={`${item.type}-${item.name}-${i}`}
          className={`w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left transition-colors ${
            i === selectedIndex
              ? 'bg-cyan-500/10 text-mono-100'
              : 'text-mono-300 hover:bg-mono-700/40'
          }`}
          onClick={() => onSelect(item)}
          onMouseEnter={() => setSelectedIndex(i)}
        >
          <span className="text-sm">{TYPE_ICONS[item.type]}</span>
          <span className="flex-1 truncate">{item.name}</span>
          <span className="text-[10px] text-mono-500">{TYPE_LABELS[item.type]}</span>
        </button>
      ))}
    </div>
  );
}

/** Hook to extract mention state from textarea value and cursor position */
export function useMentionState(value: string, cursorPos: number) {
  // Find if cursor is inside an @mention
  const beforeCursor = value.slice(0, cursorPos);
  const atIndex = beforeCursor.lastIndexOf('@');

  if (atIndex === -1) return { active: false, query: '', atIndex: -1 };

  // Check there's no space between @ and cursor (still typing the mention)
  const afterAt = beforeCursor.slice(atIndex + 1);
  if (afterAt.includes(' ') || afterAt.includes('\n')) {
    return { active: false, query: '', atIndex: -1 };
  }

  // @ must be at start or preceded by whitespace
  if (atIndex > 0 && !/\s/.test(beforeCursor[atIndex - 1])) {
    return { active: false, query: '', atIndex: -1 };
  }

  return { active: afterAt.length > 0, query: afterAt, atIndex };
}
